import React, { useMemo, useContext } from "react";
import { renderColumn, servicePick } from "utils/helpers";
import { Whisper, Tooltip, Icon } from "rsuite";
import { useIntl } from "react-intl";
import styled, { css } from "styled-components";

import { useWindowWidth } from "hooks";
import { SCREEN_MEDIA } from "const";
import { renderMoney } from "utils";
import {
    SP_KEY__INCORPORATED,
    SP_KEY__GLOBAL_ACCESS
} from "const/index";
import CustomizableTable from "components/client/CustomizableTable/CustomizableTable";
import { ButtonRowClient, ButtonToolbar } from "components/base/BaseButton";
import { RtlContext } from "../../App";
import m from "definedMessages";

const { md } = SCREEN_MEDIA;


export default (
    {
        data = [],
        loading,
        page,
        count,
        trunks,
        perPage,
        savedPerPage,
        getItems,
        onShowGetNumbersModal,
        service,
        filter,
        spAuth,
        accountInfo,
        isTest,
        isSupport,
        accountDialerState,
        perPageLSKey,
        isGlobalAccess,
        ...props
    }
) => {

    const windowWidth = useWindowWidth();
    const isMobile = windowWidth < md.min;

    const { formatMessage } = useIntl();
    const rtl = useContext(RtlContext);

    const transformedService = {
        "voice": true,
        "sms": false
    };
    const dataSpKeyList = data.map((value) => value.sp_key);

    const accountSpList = accountDialerState?.account_service_plan_list || [];

    const currentSpList = accountSpList.filter(item => {
        return filter?.sp_key_list
            ? filter.sp_key_list.includes(item.sp_key)
            : dataSpKeyList.includes(item.sp_key);
    });
    const globalAccessSp = isGlobalAccess ? accountSpList.find((value) => {
        return service ? value.sp_key === SP_KEY__INCORPORATED : value.sp_key === SP_KEY__GLOBAL_ACCESS;
    }) : null;

    const actionShowFlag = isGlobalAccess ?
        !!globalAccessSp
        && globalAccessSp.allocation_limit
        && globalAccessSp.allocation_limit.hasOwnProperty("allocate")
        && globalAccessSp.allocation_limit.allocate
        && !isTest
        : !!currentSpList.length
        && currentSpList.some((currentSp) => {
            return currentSp.allocation_limit
                && currentSp.allocation_limit.hasOwnProperty("allocate")
                && currentSp.allocation_limit.allocate
        })
        && (filter.sp_key_list
            ? !!trunks.some((trunk) => filter.sp_key_list.includes(trunk.sp_key))
            : !!trunks.some((trunk) => dataSpKeyList.includes(trunk.sp_key)))
        && !isTest;

    const columns = useMemo(() => {
        const baseColumns = [
            ...(!isGlobalAccess ? [
                {
                    id: "sp_key",
                    label: formatMessage(m.type),
                    dataKey: "sp_key",
                    value: ({ service_plan_name }) => service_plan_name,
                    width: 120
                }] : []),
            {
                id: "a_description",
                label: formatMessage(m.accessOrigin),
                dataKey: "a_description",
                value: ({ a_description, a_number }) => {
                    return (
                        <SecondStagedCell className="table-two-staged__cell">
                            <div className="table-two-staged__cell-first">
                                {a_description}
                            </div>
                            {a_number && <div className="table-two-staged__cell-second">
                                {a_number}
                            </div>}
                        </SecondStagedCell>
                    )
                },
                flexGrow: 2,
                minWidth: 200,
            },
            {
                id: "b_description",
                label: formatMessage(m.accessDestination),
                dataKey: "b_description",
                value: ({ b_description, b_number }) => {
                    return (
                        b_number ? <SecondStagedCell className="table-two-staged__cell">
                            <div className="table-two-staged__cell-first">
                                {b_description}
                            </div>
                            <div className="table-two-staged__cell-second">
                                {b_number}
                            </div>
                        </SecondStagedCell> :
                            <span>{b_description}</span>
                    )
                },
                flexGrow: 2,
                minWidth: 200,
            },
            ...(!isTest && !isSupport ? [{
                id: "payout",
                label: formatMessage(m.payout),
                value: (({ payment_terms_rate_list, sp_auth }) => {
                    let payoutOutput = "—";

                    const obj = payment_terms_rate_list ? payment_terms_rate_list.find(value => {
                        return value.payment_terms_name === (accountInfo.hasOwnProperty("payment_terms_name") ? accountInfo.payment_terms_name : "");
                    }) : null;

                    if (obj) {
                        payoutOutput = renderMoney(obj.rate, accountInfo.currency_name, 4);
                    };

                    const tooltip = <Tooltip>{formatMessage(m.pleaseNoteItHasLimitation)}</Tooltip>;
                    // Please note it has limitation for services can be used for making traffic. CLI of services you can find at Other->OTP numbers
                    return <ColumnName>
                        <ColumnNameValue>
                            {payoutOutput}
                            {sp_auth ? <Whisper placement="auto" trigger="hover" speaker={tooltip}>
                                <ColumnNameWhitelist icon="exclamation-circle2" />
                            </Whisper> : <></>}
                        </ColumnNameValue>

                    </ColumnName>;
                }),
                // ...(!isMobile ? {flexGrow: 1} : {}),
                width: 110
            }] : []),
            ...(!service ? [{
                id: "counter_day",
                label: formatMessage(m.dailyLimit),
                dataKey: "counter_day",
                value: ({ limit_day, counter_day }) => {
                    return counter_day !== null ? `${counter_day} / ${limit_day}` : "Unlimited";
                }
            },
            {
                id: "counter_hour",
                label: formatMessage(m.hourlyLimit),
                dataKey: "counter_hour",
                value: ({ limit_hour, counter_hour }) => {
                    return counter_hour !== null ? `${counter_hour} / ${limit_hour}` : "Unlimited";
                }
            }] : []),
            ...(!service ? [{
                id: "number_limit_day",
                label: formatMessage(m.limitsPerNumber),
                dataKey: "number_limit_day",
                value: ({ number_limit_day, number_limit_hour }) => {

                    const limitDay = number_limit_day ? `${number_limit_day} SMS per day` : "";
                    const limitHour = number_limit_hour ? `${number_limit_hour} SMS per hour` : "";
                    const hasBothLimits = number_limit_day && number_limit_hour;
                    const signleLimit = number_limit_day ? limitDay : number_limit_hour ? limitHour : "";

                    return hasBothLimits ? <SecondStagedCell same>
                        <div className="table-two-staged__cell-first">
                            {limitDay}
                        </div>
                        <div className="table-two-staged__cell-second">
                            {limitHour}
                        </div>
                    </SecondStagedCell>
                        : signleLimit;
                },
                ...(!isMobile ? { flexGrow: 1 } : {}),
                minWidth: rtl ? 140 : 130,
                // align: "center"
            }] : []),
            {
                id: "b_test_number_list",
                label: formatMessage(m.testNumbers),
                dataKey: "b_test_number_list",
                // flexGrow: 1,
                width: 190,
                value: ({ b_test_number_list }) => {
                    const data = b_test_number_list;
                    const firstCell = data.slice(0, 2).join(", ");
                    const secondCell = data.slice(2, 4).join(", ");
                    return (
                        data.length ? <SecondStagedCell same>
                            {data.length >= 2 && <div className="table-two-staged__cell-first">
                                {firstCell}{data.length >= 4 && ","}
                            </div>}
                            {data.length >= 4 && <div className="table-two-staged__cell-second">
                                {secondCell}
                            </div>}
                        </SecondStagedCell> :
                            <></>
                    );
                },
            },
            {
                id: "comment",
                label: formatMessage(m.comment),
                value: ({ comment }) => <span title={comment}>{comment}</span>,
                flexGrow: 3,
                minWidth: 220
            },
            ...(!isGlobalAccess ? [{
                id: "message",
                label: formatMessage(m.message),
                // dataKey: "message",
                value: ({ message }) => <span title={message}>{message}</span>,
                flexGrow: 3,
                minWidth: 220,
                hideOnService: transformedService["voice"]
            }] : []),

            ...(!isGlobalAccess ? [{
                id: "datetime",
                label: formatMessage(m.date),
                dataKey: "datetime",
                sortable: false,
                width: 155
            }] : []),
            ...(actionShowFlag ? [{
                id: "table_actions",
                ...(!rtl ? { fixed: "right" } : {}),
                label: formatMessage(m.action),
                // dataKey: "table_actions",
                value: (rowData) => {
                    const data = {
                        id: rowData.id,
                        a_description: rowData.a_description,
                        b_description: rowData.b_description,
                        a_number: rowData.a_number,
                        b_number: rowData.b_number,
                        sp_key: rowData.sp_key,
                        sde_key: rowData.sde_key,
                        ing_key: servicePick(service, rowData.ing_key, rowData["sms.supplier_gan_group_id"]),
                        gan: !!rowData["sms.supplier_gan_group_id"],
                        subdestination_name: rowData.subdestination_name
                    };

                    const trunkSpList = trunks.map((trunk) => trunk.sp_key);

                    const disabled = !trunkSpList.includes(data.sp_key);

                    return (
                        <ButtonToolbar>
                            <ButtonRowClient
                                size="sm"
                                onClick={() => {
                                    onShowGetNumbersModal(true, data);
                                }}
                                disabled={disabled}
                            >
                                <b>{formatMessage(m.getNumbers)}</b>
                            </ButtonRowClient>
                        </ButtonToolbar>
                    );
                },
                // ...(!isMobile && windowWidth > 1600 ? {flexGrow: 1} : {}),
                width: rtl ? 125 : 115,
                align: "center",
            }] : []),
        ];
        if (!isTest) {
            return baseColumns;
        }

        const uniqueTerms = [
            ...new Set(
                data
                    .filter(priceRange => priceRange && priceRange.payment_terms_rate_list)
                    .flatMap(priceRange =>
                        priceRange.payment_terms_rate_list.map(term => term.payment_terms_name)
                    )
            )
        ];

        const payoutColumns = uniqueTerms.map((term) => ({
            id: `payouts_${term}`,
            label: `${formatMessage(m.payout)} ${term}`,
            value: (({ payment_terms_rate_list }) => {
                if (!payment_terms_rate_list) {
                    return "—";
                }

                const obj = payment_terms_rate_list.find(value => value.payment_terms_name === term);
                const currencyRate = payment_terms_rate_list.find(value => value.rate !== null);
                const currency = currencyRate ? currencyRate.currency_name : null;

                if (obj && currency && accountInfo.hasOwnProperty("currency_name")) {
                    return renderMoney(obj.rate, currency, 4);
                }

                return "—";
            }),
            ...(!isMobile ? { flexGrow: 1 } : {}),
            width: 110,
            align: "center"
        }));

        return [...baseColumns, ...payoutColumns];
    }, [
        accountInfo,
        actionShowFlag,
        data,
        filter,
        formatMessage,
        isGlobalAccess,
        isMobile,
        isSupport,
        isTest,
        rtl,
        service,
        trunks,
        windowWidth,
    ]);

    const filteredColumns = columns.filter(column => ("hideOnService" in column && column?.hideOnService !== service) || !("hideOnService" in column));

    const renderedColumns = filteredColumns.map(renderColumn);

    return (
        <>
            <StyledTable
                data={data.filter(item => !!item)}
                isChangedColumns={false}
                isPaginated={true}
                rowClassName={(rowData) => {
                    if (rowData && (
                        rowData.counter_day !== null &&
                        rowData.limit_day !== null &&
                        rowData.counter_day === rowData.limit_day ||
                        rowData.counter_hour !== null &&
                        rowData.limit_hour !== null &&
                        rowData.counter_hour === rowData.limit_hour
                    ) && filter.show_accesses_without_capacity) {
                        return "red";
                    }
                }}
                {...{
                    rtl,
                    count,
                    page,
                    perPage,
                    perPageLSKey,
                    loading,
                    getItems,
                    savedPerPage,
                    renderedColumns,
                    ...props
                }}
            />
        </>
    );
};

const StyledTable = styled(CustomizableTable)`
    .rs-table-row.red .rs-table-cell {
        color: red;
    }
`;

const SecondStagedCell = styled.div`
   margin-top: -7px;
    
    .table-two-staged__cell-first {
        ${props => props.same && css`
            font-size: 13px;
        `}
    }
    .table-two-staged__cell-second {
        margin-top: -4px;
        font-size: 12px;
        ${props => !props.same ? css`
            color: var(--color-brand1);
        ` : css`
            font-size: 13px;
        `}
        opacity: 1;
    }
`;

const ColumnName = styled.div`
    display: flex;
    align-items: center;
`;

const ColumnNameValue = styled.div`
    width: 100%;
    text-align: center;
`;

const ColumnNameWhitelist = styled(Icon)`
    font-size: 12px;
    position: absolute;
    color: #4aaef4;
    margin-left: 4px;
    margin-top: 2px;
`;
